import { ScrollArea } from "@/components/ui/scroll-area";
import { useChatSession, streamChatMessage, UIContext } from "@/queries/chat";
import { useSessionData } from "@/stores/SessionDataContext";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, useCallback } from "react";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { SignalIcon } from "@/components/icons/SignalIcon";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatWindowProps {
  sessionId: number | null;
  onSessionCreated: (sessionId: number) => void;
  isCompact?: boolean;
  uiContext?: UIContext;
}

interface LocalMessage {
  role: "user" | "assistant";
  content: string;
  isLoading?: boolean;
  debugTrace?: Array<{
    function_name: string;
    trace_type?: string;
    summary_key?: string;
    cache_hit?: boolean;
    precompute_metadata?: Record<string, any>;
    precompute_payload?: Record<string, any>;
    fallback_used?: string;
    tool_params?: Record<string, any>;
    data_provenance?: Record<string, any>;
  }>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AIRPORT_SUGGESTED_PROMPTS = [
  "Show me the next 5 arrivals with passenger counts",
  "What's the domestic vs international passenger split today?",
  "Summarize arrivals vs departures by terminal",
  "Show passenger demand forecast by terminal this afternoon",
];

const AIRPORT_INPUT_PLACEHOLDER =
  "Ask about passenger demand, flights, gates, or operations...";

const RETAIL_SUGGESTED_PROMPTS = [
  "Where is my biggest retail sales opportunity from yesterday across the gates we serve?",
  "Where is my biggest retail sales opportunity today across the gates we serve?",
  "Where is my biggest retail sales opportunity tomorrow across the gates we serve?",
];

const RETAIL_INPUT_PLACEHOLDER =
  "Ask about retail sales opportunity, gate coverage, passenger demand, or hourly peaks...";

// ---------------------------------------------------------------------------
// ChatWindow
// ---------------------------------------------------------------------------

export function ChatWindow({
  sessionId,
  onSessionCreated,
  isCompact = false,
  uiContext,
}: ChatWindowProps) {
  const { isBrand } = useSessionData();
  const [localMessages, setLocalMessages] = useState<LocalMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Debug traces survive the local -> persisted-session handoff below,
  // keyed by the server's message id (stable; unlike array index it does not
  // shift as older messages page in/out). Lost on reload — debug info is a
  // live-session aid, not something worth a backend column for.
  const [debugTraceByMessageId, setDebugTraceByMessageId] = useState<
    Record<number, LocalMessage["debugTrace"]>
  >({});

  const scrollRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevSessionIdRef = useRef<number | null>(sessionId);
  // The debug trace from the turn that just finished streaming, held here
  // until the session refetch resolves and we learn the persisted message's
  // id (see the clearing effect below) — the trace can't be keyed by that id
  // any earlier, because the id does not exist until the row is written.
  const pendingDebugTraceRef = useRef<LocalMessage["debugTrace"]>(undefined);
  // Tracks how many session messages existed BEFORE we started sending.
  // Used to slice session messages so local + persisted never overlap.
  const baseMessageCountRef = useRef(0);

  const queryClient = useQueryClient();
  const { data: session, isLoading: isLoadingSession } =
    useChatSession(sessionId);
  const suggestedPrompts = isBrand
    ? RETAIL_SUGGESTED_PROMPTS
    : AIRPORT_SUGGESTED_PROMPTS;
  const inputPlaceholder = isBrand
    ? RETAIL_INPUT_PLACEHOLDER
    : AIRPORT_INPUT_PLACEHOLDER;

  // -- Derived ----------------------------------------------------------

  const sessionMessages: LocalMessage[] =
    sessionId && session?.messages
      ? session.messages
          .filter((m) => m.role !== "function" && m.content)
          .map((m) => ({
            role: m.role as "user" | "assistant",
            content: m.content,
            // The server never persists debug traces (they're a live-request-
            // only artifact) — reattach the one captured during streaming, if
            // any, so it isn't lost the moment this message stops being a
            // local optimistic entry and becomes a persisted one.
            debugTrace: debugTraceByMessageId[m.id],
          }))
      : [];

  const localNonLoadingCount = localMessages.filter((m) => !m.isLoading).length;

  // Compose the message list for display:
  // While locals exist, show session messages up to the pre-send snapshot +
  // local messages. Once the server has persisted our messages (session count
  // catches up) the clearing effect below removes locals and the full session
  // list takes over — no duplicates, no flicker.
  const allMessages =
    localMessages.length > 0
      ? [
          ...sessionMessages.slice(0, baseMessageCountRef.current),
          ...localMessages,
        ]
      : sessionMessages;

  const showWelcome = allMessages.length === 0 && !isLoadingSession;

  // -- Effects ----------------------------------------------------------

  // Clear local messages once the persisted session catches up.
  useEffect(() => {
    if (
      localNonLoadingCount > 0 &&
      !isStreaming &&
      sessionMessages.length >=
        baseMessageCountRef.current + localNonLoadingCount
    ) {
      // The just-finished turn's debug trace was only ever known locally.
      // Before dropping the local messages that carried it, file it under
      // the now-known persisted id of the assistant reply (the last
      // message — this turn is what just made sessionMessages catch up) so
      // the mapping above can still find it once sessionMessages takes over
      // rendering.
      if (pendingDebugTraceRef.current && session?.messages?.length) {
        const lastMessage = session.messages[session.messages.length - 1];
        if (lastMessage.role === "assistant") {
          const trace = pendingDebugTraceRef.current;
          setDebugTraceByMessageId((prev) => ({
            ...prev,
            [lastMessage.id]: trace,
          }));
        }
        pendingDebugTraceRef.current = undefined;
      }
      setLocalMessages([]);
      baseMessageCountRef.current = 0;
    }
  }, [localNonLoadingCount, isStreaming, sessionMessages.length, session]);

  // Handle session id transitions.
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    prevSessionIdRef.current = sessionId;

    if (prev === sessionId) return;

    // null → number: a new session was just created mid-stream.
    // Keep local messages visible until persisted data catches up.
    if (prev === null && sessionId !== null) return;

    // Any other transition (new chat, session switch): reset.
    setLocalMessages([]);
    baseMessageCountRef.current = 0;
  }, [sessionId]);

  // Cleanup abort controller on unmount.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Scroll to bottom on new messages and during streaming.
  // localMessages reference changes on every chunk, so this auto-scrolls
  // as the assistant response grows. Use instant scroll during streaming
  // to avoid janky smooth-scroll overlap on rapid updates.
  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: isStreaming ? "auto" : "smooth",
    });
  }, [localMessages, sessionMessages.length, isStreaming]);

  // -- Handlers ---------------------------------------------------------

  const handleSend = useCallback(
    async (message: string) => {
      // Cancel any in-flight stream.
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      // Snapshot current session message count before our new messages.
      baseMessageCountRef.current = sessionMessages.length;

      // Optimistic local messages: user + placeholder assistant.
      setLocalMessages((prev) => [
        ...prev,
        { role: "user", content: message },
        { role: "assistant", content: "", isLoading: true },
      ]);
      setIsStreaming(true);

      try {
        let fullResponse = "";
        let newSessionId: number | undefined;
        let debugTrace: LocalMessage["debugTrace"] = undefined;

        for await (const event of streamChatMessage({
          messages: [{ role: "user", content: message }],
          sessionId: sessionId || undefined,
          signal: abortControllerRef.current.signal,
          uiContext,
        })) {
          if (event.type === "chunk" && event.content) {
            fullResponse += event.content;
            setLocalMessages((prev) => {
              const updated = [...prev];
              const last = updated.length - 1;
              if (last >= 0 && updated[last].role === "assistant") {
                updated[last] = {
                  role: "assistant",
                  content: fullResponse,
                  isLoading: false,
                  debugTrace,
                };
              }
              return updated;
            });
          } else if (event.type === "done") {
            newSessionId = event.session_id;
            if (event.debug_trace) {
              debugTrace = event.debug_trace;
              // Handed off to the clearing effect once the session refetch
              // reveals this turn's persisted message id — see there for why.
              pendingDebugTraceRef.current = debugTrace;
              setLocalMessages((prev) => {
                const updated = [...prev];
                const last = updated.length - 1;
                if (last >= 0 && updated[last].role === "assistant") {
                  updated[last] = { ...updated[last], debugTrace };
                }
                return updated;
              });
            }
          } else if (event.type === "correction" && event.content) {
            // Backend detected a raw tool-call leak and is sending the clean
            // fallback. Replace whatever was already streamed.
            fullResponse = event.content;
            setLocalMessages((prev) => {
              const updated = [...prev];
              const last = updated.length - 1;
              if (last >= 0 && updated[last].role === "assistant") {
                updated[last] = {
                  role: "assistant",
                  content: fullResponse,
                  isLoading: false,
                  debugTrace,
                };
              }
              return updated;
            });
          } else if (event.type === "error") {
            throw new Error(event.message || "Stream error");
          }
        }

        // Notify parent of newly created session.
        if (!sessionId && newSessionId) {
          onSessionCreated(newSessionId);
        }

        // Refresh the session list (sidebar message counts, new entry) and
        // the individual session cache so that maximizing from the mini window
        // shows the latest messages immediately rather than stale cached data.
        queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
        const resolvedSessionId = sessionId ?? newSessionId;
        if (resolvedSessionId) {
          queryClient.invalidateQueries({
            queryKey: ["chat-session", resolvedSessionId],
          });
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") return;

        const errorMessage =
          (error as Error).message ||
          "Sorry, I encountered an error. Please try again.";

        setLocalMessages((prev) => {
          const updated = [...prev];
          const last = updated.length - 1;
          if (last >= 0 && updated[last].role === "assistant") {
            updated[last] = {
              role: "assistant",
              content: errorMessage,
              isLoading: false,
            };
          }
          return updated;
        });
      } finally {
        setIsStreaming(false);
      }
    },
    // uiContext must be listed: it is read inside handleSend, so without it a
    // store switch on the page kept sending the PREVIOUS store's context until
    // some other dependency happened to change.
    [
      sessionId,
      onSessionCreated,
      queryClient,
      sessionMessages.length,
      uiContext,
    ]
  );

  const handleSuggestedPrompt = (prompt: string) => {
    setShowSuggestions(false);
    handleSend(prompt);
  };

  // -- Render -----------------------------------------------------------

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-gradient-to-b from-background to-muted/20">
      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="mx-auto max-w-3xl h-full flex flex-col px-4">
          {showWelcome ? (
            <WelcomeScreen
              onPromptClick={handleSuggestedPrompt}
              isCompact={isCompact}
              suggestedPrompts={suggestedPrompts}
            />
          ) : (
            <div className="flex-1">
              <div className="pb-6 pt-4 space-y-4">
                {allMessages.map((msg, i) => (
                  <ChatMessage
                    key={i}
                    role={msg.role}
                    content={msg.content}
                    isLoading={msg.isLoading}
                    debugTrace={msg.debugTrace}
                  />
                ))}
                <div ref={scrollRef} />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input area */}
      <div className="shrink-0 border-t border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl">
          {/* Suggestion chips (unified for compact & full) */}
          {!isStreaming && (
            <div className={isCompact ? "px-4 pt-3" : "px-6 pt-4"}>
              <button
                onClick={() => setShowSuggestions((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                Suggestions
                {showSuggestions ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
              {showSuggestions && (
                <div
                  className={`flex flex-wrap gap-2 ${isCompact ? "mt-2" : "mt-3"} animate-in fade-in slide-in-from-top-1 duration-200`}
                >
                  {suggestedPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => handleSuggestedPrompt(prompt)}
                      className={
                        isCompact
                          ? "inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-background px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:bg-secondary hover:text-foreground"
                          : "inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-background px-3.5 py-1.5 text-[13px] font-medium text-muted-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:bg-secondary hover:text-foreground hover:shadow"
                      }
                    >
                      {!isCompact && (
                        <Sparkles className="h-3.5 w-3.5 text-primary" />
                      )}
                      {prompt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="px-2">
            <ChatInput
              onSend={handleSend}
              isLoading={isStreaming}
              placeholder={inputPlaceholder}
            />
          </div>
          <p className="text-[11px] text-muted-foreground/50 italic text-center pb-3 px-4">
            RaidoHub provides decision-grade forecasting intelligence;
            operational actions remain under airport authority discretion.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WelcomeScreen
// ---------------------------------------------------------------------------

interface WelcomeScreenProps {
  onPromptClick: (prompt: string) => void;
  isCompact?: boolean;
  suggestedPrompts: string[];
}

function WelcomeScreen({
  onPromptClick,
  isCompact = false,
  suggestedPrompts,
}: WelcomeScreenProps) {
  if (isCompact) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-4 py-8 animate-in fade-in zoom-in-95 duration-500">
        <div className="relative h-16 w-16 overflow-hidden rounded-2xl shadow-lg ring-1 ring-primary/20">
          <SignalIcon className="h-full w-full" />
        </div>
        <div className="text-center space-y-1.5">
          <h3 className="font-semibold text-foreground">How can I help?</h3>
          <p className="text-sm text-muted-foreground max-w-[250px]">
            Ask about flights, passengers, wait times, or generate a briefing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center p-8 animate-in fade-in zoom-in-95 duration-500">
      <div className="relative mb-8 h-32 w-32 overflow-hidden rounded-3xl shadow-lg ring-1 ring-primary/20">
        <SignalIcon className="h-full w-full" />
      </div>
      <h2 className="mb-3 text-3xl font-bold tracking-tight text-foreground">
        RaidoHub Intelligence
      </h2>
      <p className="mb-10 text-center text-base text-muted-foreground max-w-md">
        Analyze airport operations, anticipate passenger demand, and generate
        real-time operational briefings.
      </p>

      <div className="grid w-full max-w-2xl gap-4 sm:grid-cols-2">
        {suggestedPrompts.map((prompt) => (
          <button
            key={prompt}
            onClick={() => onPromptClick(prompt)}
            className="group relative flex items-start gap-3 rounded-xl border border-border/50 bg-card p-5 text-left text-sm shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/30 hover:shadow-md"
          >
            <div className="mt-0.5 rounded-full bg-primary/10 p-1.5 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
              <Sparkles className="h-4 w-4" />
            </div>
            <span className="font-medium text-muted-foreground group-hover:text-foreground transition-colors">
              {prompt}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
